import { useTranslation } from "react-i18next";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import type { TTwinKnob } from "@/zapi-frontend/shared/types/rqaSim";

type TTwinKnobProps = {
  knob: TTwinKnob;
  range: { min: number; max: number; step: number };
  readout: (value: number) => string;
  label: string;
  value: number;
  base: number;
  inert: boolean;
  readOnly: boolean;
  onChange: (value: number) => void;
};

const SLIDER =
  "relative block h-4 w-full appearance-none bg-transparent outline-hidden " +
  "[&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-full " +
  "[&::-webkit-slider-runnable-track]:bg-[linear-gradient(to_right,var(--knob)_var(--fill),var(--color-muted)_var(--fill))] " +
  "[&::-moz-range-track]:h-1.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-muted " +
  "[&::-moz-range-progress]:h-1.5 [&::-moz-range-progress]:rounded-full [&::-moz-range-progress]:bg-(--knob) " +
  "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:box-border [&::-webkit-slider-thumb]:size-4 " +
  "[&::-webkit-slider-thumb]:-mt-[5px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border " +
  "[&::-webkit-slider-thumb]:border-solid [&::-webkit-slider-thumb]:border-(--knob) [&::-webkit-slider-thumb]:bg-white " +
  "[&::-webkit-slider-thumb]:shadow-sm [&::-webkit-slider-thumb]:transition-[box-shadow] " +
  "hover:[&::-webkit-slider-thumb]:ring-4 hover:[&::-webkit-slider-thumb]:ring-ring/50 " +
  "focus-visible:[&::-webkit-slider-thumb]:ring-4 focus-visible:[&::-webkit-slider-thumb]:ring-ring/50 " +
  "[&::-moz-range-thumb]:box-border [&::-moz-range-thumb]:size-4 [&::-moz-range-thumb]:rounded-full " +
  "[&::-moz-range-thumb]:border [&::-moz-range-thumb]:border-solid [&::-moz-range-thumb]:border-(--knob) " +
  "[&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:shadow-sm";
const SLIDER_THUMB = 16;

export function TwinKnob({ knob, range, readout, label, value, base, inert, readOnly, onChange }: TTwinKnobProps) {
  const { t } = useTranslation("projectHome");
  const band = knob.standard;
  const outside = !inert && !!band && (value < band[0] || value > band[1]);
  const moved = !inert && value !== base;
  const pct = (n: number) => ((n - range.min) / (range.max - range.min)) * 100;
  // A native thumb travels from thumb/2 to width - thumb/2, so a raw percentage drifts from it
  // by up to half a thumb at the ends.
  const at = (n: number) => `calc(${pct(n)}% + (0.5 - ${pct(n) / 100}) * ${SLIDER_THUMB}px)`;
  const statusId = `rqa-sim-knob-${knob.key}-status`;
  const status = inert
    ? t("rqaSim.notInView")
    : outside && band
    ? t("rqaSim.outsideBand", { low: readout(band[0]), high: readout(band[1]) })
    : null;

  return (
    <label
      className="flex flex-col gap-3 border-b border-border py-4 last:border-b-0"
      data-testid={`rqa-sim-knob-row-${knob.key}`}
    >
      <div className={cn("flex items-start justify-between gap-3", inert && "opacity-50")}>
        <span className="typo-label-md min-w-0 text-foreground">{label}</span>
        <span className="flex shrink-0 flex-col items-end">
          <span
            className={cn(
              "typo-label-md tabular-nums",
              outside ? "text-innoveraRed-700" : moved ? "text-primary-text" : "text-foreground"
            )}
          >
            {readout(value)}
          </span>
          {moved && (
            <span className="typo-caption tabular-nums text-muted-foreground">
              {t("rqaSim.wasValue", { value: readout(base) })}
            </span>
          )}
        </span>
      </div>

      <div className={cn("relative h-4", inert && "pointer-events-none opacity-50")}>
        {band &&
          [band[0], band[1]].map((edge, index) => (
            <div
              key={index}
              aria-hidden
              className="pointer-events-none absolute top-[3px] z-10 h-2.5 w-px -translate-x-1/2 bg-greyscale-400"
              style={{ left: at(edge) }}
            />
          ))}
        <input
          type="range"
          min={range.min}
          max={range.max}
          step={range.step}
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
          disabled={inert || readOnly}
          className={cn(SLIDER, "relative z-20 cursor-pointer", readOnly && "cursor-not-allowed")}
          style={{
            ["--fill" as string]: at(value),
            ["--knob" as string]: outside ? "var(--innovera-red700)" : "var(--color-primary)"
          }}
          data-out-of-range={outside}
          data-moved={moved}
          data-testid={`rqa-sim-knob-${knob.key}`}
          aria-label={inert ? `${label} — ${t("rqaSim.notInView")}` : label}
          aria-valuetext={readout(value)}
          aria-describedby={status ? statusId : undefined}
        />
      </div>

      {status && (
        <span
          id={statusId}
          role="status"
          className={cn("typo-caption", outside ? "text-innoveraRed-700" : "text-muted-foreground")}
        >
          {status}
        </span>
      )}
    </label>
  );
}
